import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { DebugService } from '../shared/services/debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../shared/constants/app.constants.js';
import { POLICY_LIMITS_CONST } from '../shared/services/policy-limits.service.js';
import { SessionService } from '../core/services/session/session.service.js';
import { AppService } from '../app.service.js';
import { tags } from '../shared/constants/tags.js';
import { ApplicationService } from './application.service.js';
import { currencyFormatter } from '../shared/formatters/currency.formatter.js';

class _LoanService {
  constructor() {
    this.debug = new DebugService('LoanService');
    this._vehicleList = [];
    this._lookupType =
      REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.LICENSE_PLATE;
    this._selectedVehicle = {};
    this._vehicleLookupModel = {
      residenceStateCode: '',
      licensePlateNumber: '',
      licensePlateStateCode: '',
      vin: '',
    };
    this.session = SessionService.session || {};

    this.manualLoanModel = {
      currentBalance: null,
      remainingTerm: null,
      monthlyPayment: null,
      isCapitalOneLoaned: '',
      isLease: '',
      paymentDueDay: null,
    };

    this.shouldCallCustomerLoans = true;

    this.loansList = [];
  }

  getMinimumBalanceAmount() {
    return POLICY_LIMITS_CONST.MIN_PAYOFF_AMOUNT();
  }

  getMaximumBalanceAmount() {
    return POLICY_LIMITS_CONST.MAX_PAYOFF_AMOUNT();
  }

  getMaximumMonthlyPayment() {
    return POLICY_LIMITS_CONST.MAX_MONTHLY_PAYMENT();
  }

  getWarningText(loan) {
    if (loan.ineligibleReasons != null) {
      if (ApplicationService.isIconicAndToggledOn) {
        switch (loan.ineligibleReasons[0]) {
          case 'CAPITAL_ONE_LOAN':
            return "We don't refinance cars that are currently financed by Capital One";
          case 'INELIGIBLE_TERMS':
            return `We only refinance cars with a remaining term between ${POLICY_LIMITS_CONST.MIN_REMAINING_TERM()} and ${POLICY_LIMITS_CONST.MAX_REMAINING_TERM()} months`;
          case 'INELIGIBLE_LENDER':
            return `We are unable to refinance loans from this lender`;
          case 'MIN_AMOUNT':
            return `This loan is outside the limit of what we're able to lend`;
          case 'MAX_AMOUNT':
            return `This loan is outside the limit of what we're able to lend`;
          case 'AUTO_LEASE':
            return "We don't refinance leases. This car is being reported by the lender as a lease";
          case 'MONTHLY_PAYMENT':
            return `We don't refinance cars with a current monthly payment greater than ${currencyFormatter(
              POLICY_LIMITS_CONST.MAX_MONTHLY_PAYMENT()
            )}`;
          case 'HIGH_LTV':
            return `The ${
              loan.bureauUsed ? 'payoff amount' : 'balance'
            } on this loan is too high for this car`;
          default:
            return null;
        }
      } else {
        switch (loan.ineligibleReasons[0]) {
          case 'CAPITAL_ONE_LOAN':
            return "We don't refinance cars that are currently financed by Capital One.";
          case 'INELIGIBLE_TERMS':
            return `We only refinance cars with a remaining term between ${POLICY_LIMITS_CONST.MIN_REMAINING_TERM()} and ${POLICY_LIMITS_CONST.MAX_REMAINING_TERM()} months.`;
          case 'INELIGIBLE_LENDER':
            return `We are unable to refinance loans from this lender.`;
          case 'MIN_AMOUNT':
            return `This amount is outside of Capital One Auto Refinance’s amount financed limits for your loan.`;
          case 'MAX_AMOUNT':
            return `This amount is outside of Capital One Auto Refinance’s amount financed limits for your loan.`;
          case 'AUTO_LEASE':
            return "We don't refinance leases. This car is being reported by the lender as a lease.";
          case 'MONTHLY_PAYMENT':
            return `We don't refinance cars with a current monthly payment greater than ${currencyFormatter(
              POLICY_LIMITS_CONST.MAX_MONTHLY_PAYMENT()
            )}.`;
          case 'HIGH_LTV':
            return `The ${
              loan.bureauUsed ? 'payoff amount' : 'balance'
            } on this loan is too high for this vehicle.`;
          default:
            return null;
        }
      }
    }
    return null;
  }

  getIneligibleTag(loan) {
    const inEligibleTags = loan.ineligibleReasons.map((ineligibleReason) => {
      switch (ineligibleReason) {
        case 'INELIGIBLE_TERMS':
          return loan.remainingTerm > 99 ? 'term too high' : 'term too low';
        case 'MIN_AMOUNT':
          return 'balance too low';
        case 'MAX_AMOUNT':
          return 'balance too high';
        case 'AUTO_LEASE':
          return 'lease';
        case 'MONTHLY_PAYMENT':
          return 'mnthly pmt too high';
        case 'HIGH_LTV':
          return `ltv above ${POLICY_LIMITS_CONST.MAX_LTV() * 100}`;
        default: // is 'CAPITAL_ONE_LOAN'
          return 'coaf';
      }
    });
    return inEligibleTags.join(', ');
  }

  setUseManualLoanModel(useManualLoan) {
    this.useManualLoanModel = useManualLoan;
    SessionService.setUsedManualLoan(this.useManualLoanModel);
  }

  saveManualLoanModelToSession(loan) {
    let updatedLoan = loan;
    if (ApplicationService.isInAppDeclineDelayOn) {
      updatedLoan = this.checkManualLoanForDelayedDecline(loan);
    }
    ApplicationService.setSelectedLoan(true, updatedLoan);
    SessionService.setManualLoan(updatedLoan);
  }

  checkManualLoanForDelayedDecline(loan) {
    this.debug.log('Checking manual loan for delayed decline', loan);
    const loanWithIneligibleReasons = { ...loan, ineligibleReasons: [] };
    if (
      loanWithIneligibleReasons.remainingTerm &&
      (loanWithIneligibleReasons.remainingTerm <
        POLICY_LIMITS_CONST.MIN_REMAINING_TERM() ||
        loanWithIneligibleReasons.remainingTerm >
          POLICY_LIMITS_CONST.MAX_REMAINING_TERM())
    ) {
      loanWithIneligibleReasons.isEligible = false;
      loanWithIneligibleReasons.ineligibleReasons.push('INELIGIBLE_TERMS');
    }
    if (loanWithIneligibleReasons.currentBalance) {
      if (
        loanWithIneligibleReasons.currentBalance <
        this.getMinimumBalanceAmount()
      ) {
        loanWithIneligibleReasons.isEligible = false;
        loanWithIneligibleReasons.ineligibleReasons.push('MIN_AMOUNT');
      } else if (
        loanWithIneligibleReasons.currentBalance >
        this.getMaximumBalanceAmount()
      ) {
        loanWithIneligibleReasons.isEligible = false;
        loanWithIneligibleReasons.ineligibleReasons.push('MAX_AMOUNT');
      }
    }
    if (loanWithIneligibleReasons.monthlyPayment) {
      if (
        loanWithIneligibleReasons.monthlyPayment >
        this.getMaximumMonthlyPayment()
      ) {
        loanWithIneligibleReasons.isEligible = false;
        loanWithIneligibleReasons.ineligibleReasons.push('MONTHLY_PAYMENT');
      }
    }
    if (
      loanWithIneligibleReasons.isCapitalOneLoaned &&
      loan.isCapitalOneLoaned === 'yes'
    ) {
      loanWithIneligibleReasons.isEligible = false;
      loanWithIneligibleReasons.ineligibleReasons.push('CAPITAL_ONE_LOAN');
    }
    if (loanWithIneligibleReasons.isLease && loan.isLease === 'yes') {
      loanWithIneligibleReasons.isEligible = false;
      loanWithIneligibleReasons.ineligibleReasons.push('AUTO_LEASE');
    }
    return loanWithIneligibleReasons;
  }

  setSelectedLoan(loan) {
    this.debug.log('setting selectedLoan to', loan);
    this.selectedLoan = loan;
    ApplicationService.setSelectedLoan(false, loan);
    SessionService.setSelectedLoan(loan);
  }

  getManualLoanModel() {
    const loanModel = {
      currentBalance: null,
      remainingTerm: null,
      monthlyPayment: null,
      isCapitalOneLoaned: '',
      isLease: '',
      paymentDueDay: null,
      lenderName: '',
      lenderAccountNumber: '',
    };

    if (this.isUpfrontOfferEnabled()) {
      let loan = SessionService.getManualLoan();
      if (!loan) {
        loan = loanModel;
      }
      return loan;
    }
    return this.manualLoanModel;
  }

  isUpfrontOfferEnabled() {
    if (ApplicationService.isUpfrontOffers) {
      return true;
    }
    return false;
  }

  saveLoanPrequalInfo(errorMessage = '') {
    const upfrontOffer = ApplicationService.isUpfrontOffers;
    if (!upfrontOffer) {
      const sessionPrequalInfo = SessionService.getPrequalInfo() || {};
      const loanPrequalInfo = this.createPrequalInfoLoan(
        this.selectedLoan,
        errorMessage
      );
      sessionPrequalInfo.loanDetails = loanPrequalInfo.loanDetails;
      AppService.saveVehicleLoanPrequalInfo(loanPrequalInfo);
      trackAnalyticsEvent(tags.loan.refileads);
      if (!errorMessage) {
        SessionService.setPrequalInfo(loanPrequalInfo);
      }
    }
  }

  createPrequalInfoLoan(loanDetails, errorMessage = undefined) {
    const body = {};
    if (this.manualLoanModel && this.useManualLoanModel) {
      body.loanDetails = {
        currentBalanceAmount: this.manualLoanModel.currentBalance,
        monthlyPaymentAmount: this.manualLoanModel.monthlyPayment,
        remainingTerm: this.manualLoanModel.remainingTerm,
        preferredDueDay: this.manualLoanModel.paymentDueDay,
        isCapitalOneLoan: !(
          this.manualLoanModel.isCapitalOneLoaned.toUpperCase() === 'NO' ||
          this.manualLoanModel.isCapitalOneLoaned.toUpperCase() === 'FALSE'
        ),
        lenderName: this.manualLoanModel.lenderName,
        lenderAccountNumber: this.manualLoanModel.lenderAccountNumber,
        errorMessage,
      };
    } else if (loanDetails) {
      body.loanDetails = {
        currentBalanceAmount: loanDetails.currentBalance,
        monthlyPaymentAmount: loanDetails.monthlyPayment,
        remainingTerm: loanDetails.remainingTerm,
        preferredDueDay: loanDetails.paymentDueDay ?? 0,
        isCapitalOneLoan: !(
          loanDetails.isCapitalOneLoaned?.toUpperCase() === 'NO' ||
          loanDetails.isCapitalOneLoaned?.toUpperCase() === 'FALSE'
        ),
        apr: loanDetails.apr,
        lenderName: loanDetails.lenderName,
        lenderAccountNumber: loanDetails.lenderAccountNumber,
        errorMessage,
      };
    }

    return body;
  }

  setLoansList(loansList) {
    this.loansList = loansList;
  }

  getLoansList() {
    return this.loansList;
  }

  validateSelectedVehicleAndLoan(selectedVehicleAndLoan) {
    if (selectedVehicleAndLoan.vehicleDetails) {
      if (
        selectedVehicleAndLoan.vehicleDetails.year === '' ||
        selectedVehicleAndLoan.vehicleDetails.state === '' ||
        selectedVehicleAndLoan.vehicleDetails.wholesaleValueAmount === ''
      ) {
        return false;
      }
    } else {
      return false;
    }
    if (selectedVehicleAndLoan.loanDetails) {
      if (
        selectedVehicleAndLoan.loanDetails.apr === null ||
        selectedVehicleAndLoan.loanDetails.remainingTerm === null ||
        selectedVehicleAndLoan.loanDetails.currentBalanceAmount === null ||
        selectedVehicleAndLoan.loanDetails.monthlyPaymentAmount === null ||
        selectedVehicleAndLoan.loanDetails.lastPaymentDate === '' ||
        selectedVehicleAndLoan.loanDetails.lenderName === '' ||
        selectedVehicleAndLoan.loanDetails.lenderAccountNumber === ''
      ) {
        return false;
      }
    } else {
      return false;
    }

    return true;
  }

  get isManualLoan() {
    return !!ApplicationService.Application.manualEntryFlag;
  }

  resetLoanService() {
    this.debug.log('resetLoanService called');
    this.shouldCallCustomerLoans = true;
    this.loansList = [];
    this.useManualLoanModel = false;
    this.manualLoanModel = {
      currentBalance: null,
      remainingTerm: null,
      monthlyPayment: null,
      isCapitalOneLoaned: '',
      isLease: '',
      paymentDueDay: null,
    };
  }

  getSelectLoanButtonCTA(isManualLoan = false) {
    if (ApplicationService.isInAppDeclineDelayOn) {
      return isManualLoan ? 'Confirm' : 'Select this loan';
    }
    if (ApplicationService.isIconicAndToggledOn) {
      return 'Add this loan';
    }
    return isManualLoan ? 'Next' : 'Use this loan';
  }

  getSelectLoanHeader(isManualLoan = false) {
    if (ApplicationService.isInAppDeclineDelayOn) {
      return isManualLoan
        ? 'Fill out the loan information that’s connected to this car'
        : 'Select the loan that is connected to this car';
    }
    if (ApplicationService.isIconicAndToggledOn) {
      return 'Confirm the car and loan you want to refinance';
    }
    return isManualLoan
      ? 'Please tell us about the loan associated with your vehicle.'
      : 'Please confirm the loan associated with your vehicle.';
  }
}

export const LoanService = new _LoanService();
